import { useEffect, useState } from 'react';
import { fetchOrderFifoPreview, OrderFifoPreviewLine } from '../api/orderProductsApi';

export interface OrderFifoPreviewState {
  /** Per-line cost/margin of the WP5 plan. While a refetch runs, the previous answer for the same order. */
  lines: OrderFifoPreviewLine[];
  /** A request for the current lines/refreshKey has not answered yet (lines may be the previous answer). */
  loading: boolean;
  /** The request for the current lines/refreshKey failed; `lines` is then empty. */
  failed: boolean;
}

/**
 * The per-line plan preview of an order. `refreshKey` (the order object) changes after every
 * save/status change; the card keeps showing the previous lines of the SAME order until the new
 * answer arrives, so a line never flashes „—” beside tiles that already show totals. Lines are
 * cleared only when the order changes or the request fails.
 */
export function useOrderFifoPreview(orderId: string, products: readonly unknown[], enabled: boolean, refreshKey?: unknown, saved = false): OrderFifoPreviewState {
  const request = JSON.stringify(products);
  const [state, setState] = useState<{ orderId: string; request: string; refreshKey: unknown; saved: boolean; lines: OrderFifoPreviewLine[]; failed: boolean }>();
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const timer = setTimeout(() => {
      fetchOrderFifoPreview(orderId, JSON.parse(request), saved).then(
        lines => { if (active) setState({ orderId, request, refreshKey, saved, lines, failed: false }); },
        () => { if (active) setState({ orderId, request, refreshKey, saved, lines: [], failed: true }); },
      );
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [orderId, request, enabled, refreshKey, saved]);
  if (!enabled) return { lines: [], loading: false, failed: false };
  const sameOrder = state?.orderId === orderId;
  const current = sameOrder && state?.request === request && state?.refreshKey === refreshKey && state?.saved === saved;
  return {
    lines: sameOrder && state ? state.lines : [],
    loading: !current,
    failed: Boolean(current && state?.failed),
  };
}
